// The meter (time signature) a jam is counted in — a per-browser VIEW and
// PLAYBACK setting, never room state. The Ableton Extensions SDK exposes no
// signature on `Song` (only a read-only one per `Scene`), so a receiver
// could neither read a Set's meter nor write one back; a synced meter would
// be a value Live can never honour. What it governs here is what a DAW's
// meter governs on screen and in the monitor: the bars.beats clock, the
// ruler's bar lines, the piano roll's bar lines, the metronome's accent and
// count-in, and "launch on the bar". Clip positions stay in quarter-note
// beats, exactly the wire's frame, whatever the meter says.

export interface Meter {
  /** Beats per bar, in `denominator` notes: 1..99. */
  numerator: number;
  /** The note that gets the beat: 1, 2, 4, 8 or 16. */
  denominator: number;
}

export const FOUR_FOUR: Meter = { numerator: 4, denominator: 4 };

/** The meters offered outright; anything else is typed. */
export const COMMON_METERS: readonly Meter[] = [
  { numerator: 4, denominator: 4 },
  { numerator: 3, denominator: 4 },
  { numerator: 2, denominator: 4 },
  { numerator: 6, denominator: 8 },
  { numerator: 5, denominator: 4 },
  { numerator: 7, denominator: 8 },
  { numerator: 12, denominator: 8 },
  { numerator: 2, denominator: 2 },
];

const DENOMINATORS = new Set([1, 2, 4, 8, 16]);

export function isMeter(v: unknown): v is Meter {
  if (typeof v !== "object" || v === null) return false;
  const m = v as Record<string, unknown>;
  return (
    typeof m.numerator === "number" &&
    Number.isInteger(m.numerator) &&
    m.numerator >= 1 &&
    m.numerator <= 99 &&
    typeof m.denominator === "number" &&
    DENOMINATORS.has(m.denominator)
  );
}

export function sameMeter(a: Meter, b: Meter): boolean {
  return a.numerator === b.numerator && a.denominator === b.denominator;
}

/** "6/8" → a meter, or null for anything that isn't one. */
export function parseMeter(text: string): Meter | null {
  const m = /^\s*(\d{1,2})\s*\/\s*(\d{1,2})\s*$/.exec(text);
  if (!m) return null;
  const meter = { numerator: Number(m[1]), denominator: Number(m[2]) };
  return isMeter(meter) ? meter : null;
}

export function formatMeter(m: Meter): string {
  return `${m.numerator}/${m.denominator}`;
}

/** One `denominator` note, in quarter-note beats: 1 at x/4, 0.5 at x/8, 2 at x/2. */
export function beatUnit(m: Meter): number {
  return 4 / m.denominator;
}

/** A bar, in quarter-note beats: 4 at 4/4, 3 at 3/4 and at 6/8, 3.5 at 7/8. */
export function beatsPerBar(m: Meter): number {
  return m.numerator * beatUnit(m);
}

/**
 * Live's position display — `bar.beat.sixteenth`, 1-based, counted in the
 * meter's own beat: at 6/8 a beat is an eighth, so 1 quarter-note beat in
 * reads "1.3.1". 0 beats ⇒ "1.1.1"; 5.5 beats at 4/4 ⇒ "2.2.3".
 */
export function barsBeats(beats: number, meter: Meter = FOUR_FOUR): string {
  if (!Number.isFinite(beats) || beats < 0) return "1.1.1";
  const [bar, beat, tick] = split(Math.floor(beats * 4 + 1e-6), meter);
  return `${bar + 1}.${beat + 1}.${tick + 1}`;
}

/** A length in bars.beats.sixteenths (0-based, for durations): 4 beats at 4/4 ⇒ "1.0.0". */
export function barsLength(beats: number, meter: Meter = FOUR_FOUR): string {
  if (!Number.isFinite(beats) || beats < 0) return "0.0.0";
  const [bar, beat, tick] = split(Math.round(beats * 4), meter);
  return `${bar}.${beat}.${tick}`;
}

/** Sixteenths → [bars, beats within the bar, sixteenths within the beat], all
 * 0-based. A beat is `16 / denominator` sixteenths — whole for every
 * denominator offered. */
function split(sixteenths: number, meter: Meter): [number, number, number] {
  const perBeat = 16 / meter.denominator;
  const perBar = perBeat * meter.numerator;
  const bar = Math.floor(sixteenths / perBar);
  const inBar = sixteenths - bar * perBar;
  const beat = Math.floor(inBar / perBeat);
  return [bar, beat, inBar - beat * perBeat];
}
